import { createId } from "@paralleldrive/cuid2";

import type { ResumeData } from ".";
import { defaultBasics } from "./basics";
import { defaultMetadata } from "./metadata";
import {
  defaultEducation,
  defaultExperience,
  defaultProfile,
  defaultProject,
  defaultSections,
  defaultSkill,
} from "./sections";
import { applyCalendarDates, rangeTenureMonths } from "./dates";
import { defaultUrl } from "./shared";

export const SAMPLE_IDENTITY_NAMES = ["john doe", "jane doe"];
export const SAMPLE_COMPANIES = ["creative solutions inc.", "techadvancers"];
export const SAMPLE_LOCATIONS = ["pleasantville"];

const SCHOOL_WORD_RE = /\b(college|university|school|institute|academy|polytechnic)\b/i;

const KNOWN_ORGS: Record<string, string> = {
  facebook: "Facebook",
  meta: "Meta",
  google: "Google",
  alphabet: "Alphabet",
  amazon: "Amazon",
  apple: "Apple",
  netflix: "Netflix",
  microsoft: "Microsoft",
  stripe: "Stripe",
  figma: "Figma",
  notion: "Notion",
  airbnb: "Airbnb",
  uber: "Uber",
  lyft: "Lyft",
  linkedin: "LinkedIn",
  github: "GitHub",
  openai: "OpenAI",
  nvidia: "NVIDIA",
  tesla: "Tesla",
  shopify: "Shopify",
  slack: "Slack",
  spotify: "Spotify",
  twitter: "Twitter",
  stanford: "Stanford University",
  mit: "MIT",
  harvard: "Harvard",
  yale: "Yale",
  princeton: "Princeton",
  berkeley: "UC Berkeley",
  ucla: "UCLA",
  nyu: "NYU",
  cmu: "Carnegie Mellon",
  caltech: "Caltech",
  oxford: "Oxford",
  cambridge: "Cambridge",
};

const KNOWN_SCHOOLS = new Set([
  "stanford",
  "mit",
  "harvard",
  "yale",
  "princeton",
  "berkeley",
  "uc berkeley",
  "ucla",
  "nyu",
  "cmu",
  "carnegie mellon",
  "caltech",
  "oxford",
  "cambridge",
  "columbia",
  "cornell",
  "brown",
  "dartmouth",
  "uchicago",
  "university of chicago",
  "georgia tech",
  "uiuc",
  "usc",
  "ut austin",
  "city college",
]);

const titleCaseName = (value: string): string =>
  value
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const canonicalizeOrgName = (value: string): string => {
  const trimmed = value.replace(/\s+/g, " ").trim();
  if (!trimmed) return "";
  const known = KNOWN_ORGS[trimmed.toLowerCase()];
  if (known) return known;
  if (
    /[a-z][A-Z]/.test(trimmed) ||
    trimmed === trimmed.toLowerCase() ||
    trimmed === trimmed.toUpperCase()
  ) {
    return titleCaseName(trimmed);
  }
  return trimmed;
};

export const isLikelySchool = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (SCHOOL_WORD_RE.test(trimmed)) return true;
  return KNOWN_SCHOOLS.has(trimmed.toLowerCase());
};

export const matchKnownSchool = (text: string): string | null => {
  for (const school of KNOWN_SCHOOLS) {
    const pattern = new RegExp(`\\b${school.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    if (pattern.test(text)) return canonicalizeOrgName(school);
  }
  return null;
};

export const schoolIdentityKey = (value: string): string =>
  canonicalizeOrgName(value)
    .toLowerCase()
    .replace(/\b(the|of|at)\b/g, " ")
    .replace(/\b(university|college|institute|academy)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const isAcademicRole = (position: string): boolean =>
  /\b(professor|lecturer|instructor|researcher|scientist|fellow|teaching assistant|\bta\b|postdoc|faculty)\b/i.test(
    position,
  );

export const looksLikePlaceholder = (value: string): boolean => /\[[^\]]+\]/.test(value);

export const isJunkPosition = (position: string): boolean => {
  const value = position.replace(/^[,.;:\s]+/, "").trim();
  if (!value || value.length < 2 || value.length > 60) return true;
  if (looksLikePlaceholder(value)) return true;
  if (/^(after|before|then|and|with|for|from|the|a|an)\b/i.test(value)) return true;
  if (/\b(stud(?:y|ied|ying)|graduat|degree from)\b/i.test(value)) return true;
  if (/^(skills?|education|experience|summary|contact|profile)\b/i.test(value)) return true;
  return false;
};

export type DraftUrl = { label?: string; href?: string } | string;

export type DraftExperience = {
  company?: string;
  position?: string;
  date?: string;
  location?: string;
  summary?: string;
  url?: DraftUrl;
};

export type DraftEducation = {
  institution?: string;
  studyType?: string;
  area?: string;
  score?: string;
  date?: string;
  summary?: string;
  url?: DraftUrl;
};

export type DraftSkill = {
  name?: string;
  description?: string;
  level?: number;
  keywords?: string[];
};

export type DraftProject = {
  name?: string;
  description?: string;
  date?: string;
  summary?: string;
  keywords?: string[];
  url?: DraftUrl;
};

export type DraftProfile = {
  network?: string;
  username?: string;
  icon?: string;
  url?: DraftUrl;
};

export type ResumeDraft = {
  basics?: {
    name?: string;
    headline?: string;
    email?: string;
    phone?: string;
    location?: string;
    url?: DraftUrl;
  };
  summary?: string;
  experience?: DraftExperience[];
  education?: DraftEducation[];
  skills?: DraftSkill[];
  projects?: DraftProject[];
  profiles?: DraftProfile[];
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

export const isValidEmail = (value: string | undefined | null): value is string =>
  Boolean(value && EMAIL_RE.test(value.trim()));

export const isSampleName = (name: string | undefined | null): boolean =>
  SAMPLE_IDENTITY_NAMES.includes((name ?? "").trim().toLowerCase());

export const isSampleCompany = (company: string | undefined | null): boolean =>
  SAMPLE_COMPANIES.includes((company ?? "").trim().toLowerCase());

export const isRealExperience = (company: string | undefined | null): boolean => {
  const value = (company ?? "").trim();
  return value.length >= 2 && !isSampleCompany(value) && !looksLikePlaceholder(value);
};

export const isValidExperienceItem = (item: DraftExperience): boolean => {
  const company = item.company?.trim() ?? "";
  const position = item.position?.trim() ?? "";
  if (!isRealExperience(company)) return false;
  if (isJunkPosition(position)) return false;
  if (isLikelySchool(company) && !isAcademicRole(position)) return false;
  return true;
};

export const looksLikeSampleResume = (data: ResumeData): boolean => {
  if (isSampleName(data.basics.name)) return true;
  if (SAMPLE_LOCATIONS.some((loc) => data.basics.location.toLowerCase().includes(loc))) return true;
  return data.sections.experience.items.some((item) => isSampleCompany(item.company));
};

export const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const toHtmlSummary = (value: string | undefined | null): string => {
  const text = (value ?? "").trim();
  if (!text) return "";
  const lines = extractDutyLines(text);
  if (lines.length === 0) return "";
  if (lines.length === 1 && !/<li\b/i.test(text)) return `<p>${escapeHtml(lines[0])}</p>`;
  return toBulletHtml(lines);
};

const normalizeHref = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.includes(".") && !trimmed.includes(" ")) return `https://${trimmed}`;
  return "";
};

export const normalizeDraftUrl = (value: DraftUrl | undefined) => {
  if (!value) return { ...defaultUrl };
  if (typeof value === "string") {
    return { label: "", href: normalizeHref(value) };
  }
  return {
    label: typeof value.label === "string" ? value.label : "",
    href: typeof value.href === "string" ? normalizeHref(value.href) : "",
  };
};

const pickName = (draftName: string | undefined, userName: string | undefined): string => {
  const fromDraft = draftName?.trim() ?? "";
  if (fromDraft && !isSampleName(fromDraft)) return fromDraft;
  const fromUser = userName?.trim() ?? "";
  if (fromUser) return fromUser;
  return fromDraft;
};

const pickEmail = (draftEmail: string | undefined, userEmail: string | undefined): string => {
  if (isValidEmail(draftEmail)) return draftEmail.trim();
  if (isValidEmail(userEmail)) return userEmail.trim();
  return "";
};

type UserIdentity = {
  name?: string | null;
  email?: string | null;
  picture?: string | null;
};

export const emptyResumeDataForUser = (user: UserIdentity = {}): ResumeData => {
  const data = clone({
    basics: defaultBasics,
    sections: defaultSections,
    metadata: defaultMetadata,
  } satisfies ResumeData);

  data.basics.name = user.name?.trim() ?? "";
  data.basics.email = isValidEmail(user.email) ? user.email.trim() : "";
  data.basics.picture = {
    ...data.basics.picture,
    url: user.picture ?? "",
  };

  return data;
};

export const applyDraftToResumeData = (base: ResumeData, draft: ResumeDraft): ResumeData => {
  const next = clone(base);
  const basics = draft.basics ?? {};

  next.basics.name = pickName(basics.name, next.basics.name);
  next.basics.headline = basics.headline?.trim() || next.basics.headline;
  next.basics.email = pickEmail(basics.email, next.basics.email);
  next.basics.phone = basics.phone?.trim() || next.basics.phone;
  next.basics.location = basics.location?.trim() || next.basics.location;

  if (basics.url) {
    const url = normalizeDraftUrl(basics.url);
    if (url.href) next.basics.url = url;
  }

  if (draft.summary?.trim()) {
    next.sections.summary.content = toHtmlSummary(draft.summary);
  }

  const experience = (draft.experience ?? [])
    .filter((item) => isRealExperience(item.company))
    .map((item) => ({
      ...clone(defaultExperience),
      id: createId(),
      visible: true,
      company: item.company!.trim(),
      position: item.position?.trim() ?? "",
      date: item.date?.trim() ?? "",
      location: item.location?.trim() ?? "",
      summary: toHtmlSummary(item.summary),
      url: normalizeDraftUrl(item.url),
    }));

  if (experience.length > 0) {
    next.sections.experience.items = experience;
  }

  const education = (draft.education ?? [])
    .filter((item) => (item.institution ?? "").trim().length > 0)
    .map((item) => ({
      ...clone(defaultEducation),
      id: createId(),
      visible: true,
      institution: item.institution!.trim(),
      studyType: item.studyType?.trim() ?? "",
      area: item.area?.trim() ?? "",
      score: item.score?.trim() ?? "",
      date: item.date?.trim() ?? "",
      summary: toHtmlSummary(item.summary),
      url: normalizeDraftUrl(item.url),
    }));

  if (education.length > 0) {
    next.sections.education.items = education;
  }

  const skills = (draft.skills ?? [])
    .map((item) => {
      const keywords = (item.keywords ?? []).map((keyword) => keyword.trim()).filter(Boolean);
      const name = item.name?.trim() ?? "";
      if (!name && keywords.length === 0) return null;
      return {
        ...clone(defaultSkill),
        id: createId(),
        visible: true,
        name: isGenericSkillName(name) ? "" : name,
        description: item.description?.trim() ?? "",
        level: Math.min(5, Math.max(0, Math.round(item.level ?? 0))),
        keywords,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (skills.length > 0) {
    next.sections.skills.items = skills;
  }

  const projects = (draft.projects ?? [])
    .filter((item) => (item.name ?? "").trim().length > 0)
    .map((item) => ({
      ...clone(defaultProject),
      id: createId(),
      visible: true,
      name: item.name!.trim(),
      description: item.description?.trim() ?? "",
      date: item.date?.trim() ?? "",
      summary: toHtmlSummary(item.summary),
      keywords: (item.keywords ?? []).map((keyword) => keyword.trim()).filter(Boolean),
      url: normalizeDraftUrl(item.url),
    }));

  if (projects.length > 0) {
    next.sections.projects.items = projects;
  }

  const profiles = (draft.profiles ?? [])
    .filter(
      (item) => (item.network ?? "").trim().length > 0 && (item.username ?? "").trim().length > 0,
    )
    .map((item) => ({
      ...clone(defaultProfile),
      id: createId(),
      visible: true,
      network: item.network!.trim(),
      username: item.username!.trim(),
      icon: item.icon?.trim() || item.network!.trim().toLowerCase(),
      url: normalizeDraftUrl(item.url),
    }));

  if (profiles.length > 0) {
    next.sections.profiles.items = profiles;
  }

  return next;
};

export const createResumeDataFromDraft = (
  draft: ResumeDraft,
  user: UserIdentity = {},
  options: { template?: string } = {},
): ResumeData => {
  const data = applyDraftToResumeData(
    emptyResumeDataForUser(user),
    applyCalendarDates(draft),
  );
  if (options.template) data.metadata.template = options.template;
  return data;
};

export const draftHasExperience = (draft: ResumeDraft): boolean =>
  (draft.experience ?? []).some((item) => isRealExperience(item.company));

export const draftHasStructuredContent = (draft: ResumeDraft): boolean =>
  draftHasExperience(draft) ||
  (draft.education ?? []).some((item) => (item.institution ?? "").trim().length > 0) ||
  (draft.skills ?? []).some((item) => (item.keywords ?? []).some((keyword) => keyword.trim()));

const stripTags = (value?: string): string =>
  (value ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const GERUND_LEAD: Record<string, string> = {
  shipping: "Shipped",
  cutting: "Cut",
  reducing: "Reduced",
  leading: "Lead",
  handling: "Handled",
  resolving: "Resolved",
  maintaining: "Maintained",
  building: "Built",
  writing: "Wrote",
};

const splitDutySentences = (text: string): string[] => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (!cleaned) return [];
  const parts = cleaned
    .split(/(?<=[.!?])\s+(?=[A-Z])/)
    .flatMap((part) =>
      part.split(
        /,\s+(?=(?:shipping|cutting|reducing|leading|handling|resolving|maintaining|building|writing)\b)/i,
      ),
    )
    .map((item) => item.replace(/^[,.;:\s]+/, "").replace(/[.]+$/, "").trim())
    .filter((item) => item.length >= 12)
    .map((item) => {
      const gerund = item.match(/^([a-z]+ing)\b/i)?.[1]?.toLowerCase() ?? "";
      if (gerund && GERUND_LEAD[gerund]) {
        return `${GERUND_LEAD[gerund]}${item.slice(gerund.length)}`;
      }
      return `${item.charAt(0).toUpperCase()}${item.slice(1)}`;
    });
  return parts.length > 0 ? parts : [cleaned.replace(/[.]+$/, "").trim()].filter(Boolean);
};

export const extractDutyLines = (value?: string): string[] => {
  const html = value ?? "";
  const items = [...html.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((match) => stripTags(match[1]))
    .filter(Boolean);
  const raw =
    items.length > 0
      ? items
      : html
          .split(/\n+/)
          .map((line) => stripTags(line.replace(/^[-*•]\s*/, "")))
          .filter(Boolean);
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const line of raw.flatMap(splitDutySentences)) {
    const key = line.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(line);
  }
  return unique;
};

export const isGenericSkillName = (name?: string): boolean =>
  !name?.trim() || /^(skills?|technical skills|core skills)$/i.test(name.trim());

export const isHighSchoolEducation = (item: DraftEducation): boolean =>
  /\b(high school|secondary school)\b/i.test(
    `${item.institution ?? ""} ${item.studyType ?? ""} ${item.area ?? ""}`,
  );

export const preferCollegeEducation = (education: DraftEducation[] = []): DraftEducation[] => {
  const list = education.filter((item) => item.institution?.trim());
  const college = list.filter((item) => !isHighSchoolEducation(item));
  return college.length > 0 ? college : list;
};

const preferLongerText = (left?: string, right?: string): string => {
  const a = left?.trim() ?? "";
  const b = right?.trim() ?? "";
  return a.length >= b.length ? a || b : b;
};

const degreeRank = (value: string): number => {
  if (/\b(ph\.?d|doctorate|doctoral)\b/i.test(value)) return 4;
  if (/\bmba\b/i.test(value)) return 3;
  if (/\bmaster/i.test(value)) return 2;
  return value.trim() ? 1 : 0;
};

const preferDegree = (left?: string, right?: string): string => {
  const a = left?.trim() ?? "";
  const b = right?.trim() ?? "";
  return degreeRank(a) >= degreeRank(b) ? a || b : b;
};

const inferBachelors = (institution?: string, area?: string, studyType?: string): string => {
  if (studyType?.trim()) return studyType.trim();
  if (!area?.trim() || !institution) return "";
  if (/\b(master|ph\.?d|mba|associate)\b/i.test(area)) return "";
  if (/\b(community college|city college)\b/i.test(institution)) return "";
  if (
    /\buniversity\b/i.test(institution) ||
    KNOWN_SCHOOLS.has(schoolIdentityKey(institution))
  ) {
    return "Bachelor's";
  }
  return "";
};

export const collapseEducation = (education: DraftEducation[] = []): DraftEducation[] => {
  const byKey = new Map<string, DraftEducation>();
  for (const item of education) {
    const institution = canonicalizeOrgName(item.institution ?? "");
    if (!institution) continue;
    const key = schoolIdentityKey(institution);
    if (!key) continue;
    const current = byKey.get(key);
    const area = preferLongerText(item.area, current?.area);
    const date = item.date?.trim() || current?.date || "";
    const nextInstitution =
      institution.length >= (current?.institution?.length ?? 0) ? institution : current?.institution;
    const studyType = preferDegree(item.studyType, current?.studyType);
    byKey.set(key, {
      ...current,
      ...item,
      institution: nextInstitution,
      studyType,
      area,
      date,
    });
  }
  return [...byKey.values()].map((item) => ({
    ...item,
    studyType: inferBachelors(item.institution, item.area, item.studyType),
  }));
};

export const experienceHasWrittenWork = (summary?: string): boolean =>
  stripTags(summary).length >= 24;

export const draftHasWrittenWork = (draft: ResumeDraft): boolean =>
  stripTags(draft.summary).length >= 40 ||
  (draft.experience ?? []).some((item) => experienceHasWrittenWork(item.summary));

const TITLE_WORK: Array<[RegExp, string[]]> = [
  [
    /barista|bartender|bar staff/i,
    [
      "Prepared drinks to order, kept the station stocked, and left it ready for the next person",
      "Served guests, handled payments, and stepped in when the line got long",
      "Closed down the bar and passed context to the next shift",
      "Learned the house recipes and kept quality consistent during busy hours",
    ],
  ],
  [
    /shift (lead|supervisor)|supervisor|team lead|floor lead/i,
    [
      "Ran the floor during service and kept the team on task",
      "Opened or closed the shop and handled handoff between shifts",
      "Stepped in when the line got busy and coached newer staff",
      "Tracked what ran out and restocked before the next rush",
    ],
  ],
  [
    /server|waiter|waitress|host/i,
    [
      "Took orders and kept tables moving through the shift",
      "Handled guest requests and checked in during service",
      "Reset tables and helped close the floor",
    ],
  ],
  [
    /cashier|retail|sales associate|store associate/i,
    [
      "Helped customers find what they needed and rang up sales",
      "Kept the floor stocked and the checkout moving",
      "Closed the register and left the station ready for the next shift",
    ],
  ],
  [
    /(?:^|\b)intern(?:\b|$)/i,
    [
      "Shipped assigned product work with the team's existing tools and stack",
      "Wrote tests and fixed bugs on the features they touched",
      "Turned specs and review notes into the next pass of the work",
      "Documented what they shipped so others could pick it up",
    ],
  ],
  [
    /\bnurse\b|\bnursing assistant\b|\bcna\b/i,
    [
      "Supported patient care and kept rooms ready between visits",
      "Recorded routine checks and flagged changes to the team",
      "Helped patients with day-to-day needs during the shift",
    ],
  ],
  [
    /engineer|developer|programmer|frontend|backend/i,
    [
      "Designed, built, and shipped product features with engineering, design, and product",
      "Debugged production issues, wrote tests, and kept services stable",
      "Reviewed code and left notes so others could follow the work",
      "Broke down scope, named tradeoffs, and delivered on agreed timelines",
      "Worked across the stack the team already uses and documented decisions",
    ],
  ],
  [
    /account manager|customer success|customer service|support/i,
    [
      "Handled customer questions and followed issues through to a close",
      "Kept accounts updated and passed context to the rest of the team",
      "Stepped in when volume spiked and kept response times in line",
    ],
  ],
];

const typicalWorkForTitle = (position: string): string[] => {
  const title = position.trim();
  for (const [pattern, bullets] of TITLE_WORK) {
    if (pattern.test(title)) return bullets;
  }
  const label = title || "this role";
  return [
    `Handled the day-to-day work of a ${label}`,
    "Supported the team during busy stretches",
    "Kept the work accurate and on time",
    "Stepped in when something slipped and closed it out",
  ];
};

const JUNIOR_TITLE =
  /\b(junior|jr\.?|associate|assistant|coordinator|trainee|apprentice)\b/i;
const SENIOR_TITLE =
  /\b(senior|sr\.?|staff|principal|lead|manager|head of|director|vp|chief)\b/i;
const INTERN_TITLE = /\bintern(?:ship)?\b/i;

export const targetBulletCount = (
  item: { position?: string; date?: string },
  index: number,
  now: Date = new Date(),
): number => {
  const title = item.position ?? "";
  const months = rangeTenureMonths(item.date ?? "", now);
  const intern = INTERN_TITLE.test(title);
  const junior = JUNIOR_TITLE.test(title);
  const senior = SENIOR_TITLE.test(title);

  let count = 4;
  if (intern) count = months !== null && months >= 6 ? 3 : 2;
  else if (junior) count = months !== null && months >= 18 ? 4 : 3;
  else if (senior) count = months !== null && months < 12 ? 3 : months !== null && months >= 24 ? 5 : 4;
  else if (months !== null && months < 6) count = 2;
  else if (months !== null && months < 24) count = 3;
  else count = 4;

  if (index > 0) count = Math.max(2, count - 1);
  return Math.min(5, count);
};

const bulletsOverlap = (left: string, right: string): boolean => {
  const tokens = (value: string) =>
    new Set(
      value
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((word) => word.length > 3),
    );
  const a = tokens(left);
  let hits = 0;
  for (const word of tokens(right)) {
    if (a.has(word)) hits += 1;
  }
  return hits >= 3;
};

const firstVerb = (line: string): string => line.trim().split(/\s+/)[0]?.toLowerCase() ?? "";

const normalizeRoleSummary = (
  summary: string | undefined,
  position: string,
  target: number,
): string => {
  const typical = typicalWorkForTitle(position);
  const lines = extractDutyLines(summary);
  const isTypical = (line: string) =>
    typical.some((item) => item.toLowerCase() === line.toLowerCase());
  const specific = lines.filter((line) => !isTypical(line));
  const discardedTypical = lines.filter(isTypical);
  const core = specific.length > 0 ? specific : lines;
  if (core.length >= target) return toBulletHtml(core.slice(0, target));
  if (specific.length >= 3) return toBulletHtml(core.slice(0, target));
  const usedVerbs = new Set(core.map(firstVerb).filter(Boolean));
  const extra = typical
    .filter((line) => !usedVerbs.has(firstVerb(line)))
    .filter((line) => !core.some((item) => bulletsOverlap(item, line)))
    .filter((line) => !discardedTypical.some((item) => item.toLowerCase() === line.toLowerCase()))
    .slice(0, target - core.length);
  return toBulletHtml([...core, ...extra].slice(0, target));
};

const toBulletHtml = (lines: string[]): string =>
  `<ul>${lines.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>`;

const TITLE_SKILLS: Array<[RegExp, DraftSkill[]]> = [
  [
    /engineer|developer|programmer|frontend|backend/i,
    [
      { name: "Languages", keywords: ["TypeScript", "Python", "SQL", "JavaScript"], level: 0 },
      { name: "Tools", keywords: ["Git", "React", "APIs", "SQL"], level: 0 },
      { name: "Practices", keywords: ["Code review", "Testing", "Debugging", "Documentation"], level: 0 },
    ],
  ],
  [/intern/i, [{ name: "Tools", keywords: ["Git", "Documentation", "Spreadsheets"], level: 0 }]],
  [
    /barista|bartender|shift (lead|supervisor)|supervisor|server|cashier|retail/i,
    [{ name: "Service", keywords: ["Customer service", "POS", "Cash handling"], level: 0 }],
  ],
  [
    /nurse|nursing/i,
    [{ name: "Care", keywords: ["Patient care", "Charting", "Vital signs"], level: 0 }],
  ],
];

const relocateSchools = (draft: ResumeDraft): ResumeDraft => {
  const education = [...(draft.education ?? [])];
  const experience: DraftExperience[] = [];

  for (const item of draft.experience ?? []) {
    const company = canonicalizeOrgName(item.company ?? "");
    const position = item.position?.trim() ?? "";
    if (company && isLikelySchool(company) && !isAcademicRole(position)) {
      if (!education.some((row) => row.institution?.toLowerCase() === company.toLowerCase())) {
        education.push({
          institution: company,
          date: item.date ?? "",
          area: "",
          studyType: "",
        });
      }
      continue;
    }
    experience.push({ ...item, company });
  }

  return {
    ...draft,
    experience,
    education: education.map((item) => ({
      ...item,
      institution: canonicalizeOrgName(item.institution ?? ""),
    })),
  };
};

const mergeExperience = (
  base: ResumeDraft["experience"] = [],
  overlay: ResumeDraft["experience"] = [],
): NonNullable<ResumeDraft["experience"]> => {
  const baseItems = base.filter(isValidExperienceItem);
  const overlayItems = overlay.filter(isValidExperienceItem);
  if (overlayItems.length === 0) return baseItems;
  if (baseItems.length === 0) return overlayItems;

  return overlayItems.map((item) => {
    const company = canonicalizeOrgName(item.company ?? "");
    const match =
      baseItems.find(
        (row) =>
          canonicalizeOrgName(row.company ?? "").toLowerCase() === company.toLowerCase() &&
          (row.position ?? "").trim().toLowerCase() === (item.position ?? "").trim().toLowerCase(),
      ) ??
      baseItems.find(
        (row) => canonicalizeOrgName(row.company ?? "").toLowerCase() === company.toLowerCase(),
      );
    if (!match) return { ...item, company };
    return {
      ...match,
      ...item,
      company,
      date: match.date?.trim() || item.date,
      location: item.location?.trim() || match.location,
      position: item.position?.trim() || match.position,
      summary: experienceHasWrittenWork(item.summary) ? item.summary : match.summary,
    };
  });
};

const preferLocation = (base?: string, overlay?: string): string | undefined => {
  const next = overlay?.trim() ?? "";
  const current = base?.trim() ?? "";
  if (!next) return current || undefined;
  if (!current) return next;
  if (
    next.toLowerCase().startsWith(current.toLowerCase()) &&
    /,\s*[A-Z]{2}$/.test(next) &&
    !/,\s*[A-Z]{2}$/.test(current)
  ) {
    return current;
  }
  return next;
};

const mergeEducation = (
  base: ResumeDraft["education"] = [],
  overlay: ResumeDraft["education"] = [],
): NonNullable<ResumeDraft["education"]> => {
  const list = [...base];
  for (const item of overlay) {
    const institution = canonicalizeOrgName(item.institution ?? "");
    if (!institution) continue;
    const index = list.findIndex(
      (row) => schoolIdentityKey(row.institution ?? "") === schoolIdentityKey(institution),
    );
    if (index < 0) {
      list.push({ ...item, institution });
      continue;
    }
    list[index] = {
      ...list[index],
      ...item,
      institution:
        institution.length >= (list[index]?.institution?.length ?? 0)
          ? institution
          : list[index]?.institution,
      area: preferLongerText(item.area, list[index]?.area),
      date: item.date?.trim() || list[index]?.date,
      studyType: preferDegree(item.studyType, list[index]?.studyType),
    };
  }
  return collapseEducation(list);
};

export const mergeResumeDraft = (base: ResumeDraft, overlay: ResumeDraft): ResumeDraft => {
  const overlayName = overlay.basics?.name?.trim();
  const overlayEmail = overlay.basics?.email?.trim();
  const overlayExperience = (overlay.experience ?? []).filter(isValidExperienceItem);
  const baseExperience = (base.experience ?? []).filter(isValidExperienceItem);

  return {
    ...base,
    ...overlay,
    basics: {
      ...base.basics,
      ...overlay.basics,
      name: overlayName && !isSampleName(overlayName) ? overlayName : base.basics?.name,
      email: isValidEmail(overlayEmail) ? overlayEmail : base.basics?.email,
      phone: overlay.basics?.phone?.trim() || base.basics?.phone,
      headline: overlay.basics?.headline?.trim() || base.basics?.headline,
      location: preferLocation(base.basics?.location, overlay.basics?.location),
    },
    experience: mergeExperience(baseExperience, overlayExperience),
    education: mergeEducation(base.education, overlay.education),
    skills: overlay.skills?.length ? overlay.skills : base.skills,
    projects: overlay.projects?.length ? overlay.projects : base.projects,
    profiles: overlay.profiles?.length ? overlay.profiles : base.profiles,
    summary: overlay.summary?.trim() || base.summary,
  };
};

const LANGUAGE_SKILL_RE =
  /^(python|javascript|typescript|java|sql|go|golang|rust|ruby|php|c\+\+|c#|kotlin|swift|html|css|r|scala|bash|shell)$/i;

const uniqueKeywords = (items: string[]): string[] => {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const item of items) {
    const value = item.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(value);
  }
  return next;
};

const inferSkills = (draft: ResumeDraft): DraftSkill[] => {
  const named = uniqueKeywords([
    ...(draft.skills ?? []).flatMap((item) => item.keywords ?? []),
    ...(draft.projects ?? []).flatMap((item) => item.keywords ?? []),
  ]);

  const haystack = [
    draft.basics?.headline,
    ...(draft.experience ?? []).map((item) => item.position),
    ...(draft.education ?? []).map((item) => item.area),
  ]
    .filter(Boolean)
    .join(" ");

  let typical: DraftSkill[] = [];
  for (const [pattern, skills] of TITLE_SKILLS) {
    if (pattern.test(haystack)) {
      typical = skills.map((item) => ({ ...item, keywords: [...(item.keywords ?? [])] }));
      break;
    }
  }

  if (typical.length === 0) {
    return named.length > 0 ? [{ name: "", keywords: named, level: 0 }] : [];
  }
  if (named.length === 0) return typical;

  const languages = named.filter((item) => LANGUAGE_SKILL_RE.test(item));
  const rest = named.filter((item) => !LANGUAGE_SKILL_RE.test(item));

  return typical.map((group) => {
    if (/language/i.test(group.name ?? "") && languages.length > 0) {
      return { ...group, keywords: uniqueKeywords([...languages, ...(group.keywords ?? [])]).slice(0, 8) };
    }
    if (/tool/i.test(group.name ?? "") && rest.length > 0) {
      return { ...group, keywords: uniqueKeywords([...rest, ...(group.keywords ?? [])]).slice(0, 8) };
    }
    return { ...group, keywords: uniqueKeywords(group.keywords ?? []).slice(0, 8) };
  });
};

const writeSummary = (draft: ResumeDraft): string => {
  const existing = stripTags(draft.summary);
  if (existing.length >= 140) return draft.summary ?? "";

  const headline = draft.basics?.headline?.trim() || draft.experience?.[0]?.position?.trim();
  const roles = (draft.experience ?? []).filter((item) => item.company?.trim());
  const school = draft.education?.[0];
  const skills = uniqueKeywords((draft.skills ?? []).flatMap((item) => item.keywords ?? [])).slice(0, 4);
  const parts: string[] = [];

  if (headline && roles[0]?.company) {
    const prior = roles[1]?.company
      ? `, previously ${[roles[1].position, roles[1].company].filter(Boolean).join(" at ")}`
      : "";
    parts.push(`${headline} at ${roles[0].company}${prior}.`);
  } else if (headline) {
    parts.push(`${headline}.`);
  }
  if (school?.institution) {
    const field = [school.studyType, school.area].filter(Boolean).join(" ");
    parts.push(field ? `${field} at ${school.institution}.` : `Studies at ${school.institution}.`);
  }
  if (skills.length > 0) {
    parts.push(`Works with ${skills.join(", ")}.`);
  }
  return parts.join(" ");
};

export const seedTypicalWork = (draft: ResumeDraft, now: Date = new Date()): ResumeDraft => {
  const relocated = relocateSchools(draft);
  const experience = (relocated.experience ?? [])
    .filter(isValidExperienceItem)
    .map((item) => ({
      ...item,
      company: canonicalizeOrgName(item.company ?? ""),
    }));
  const next: ResumeDraft = applyCalendarDates(
    {
      ...relocated,
      basics: { ...relocated.basics },
      experience,
      education: collapseEducation(preferCollegeEducation(relocated.education)),
      skills: inferSkills(relocated),
      projects: [...(relocated.projects ?? [])],
    },
    now,
  );

  if (!next.basics?.headline && next.experience?.[0]?.position) {
    next.basics = { ...next.basics, headline: next.experience[0].position };
  }

  next.experience = (next.experience ?? []).map((item, index) => {
    const target = targetBulletCount(item, index, now);
    return {
      ...item,
      summary: normalizeRoleSummary(item.summary, item.position ?? "", target),
    };
  });

  next.projects = (next.projects ?? []).map((item) => {
    const lines = extractDutyLines(item.summary);
    if (lines.length > 0) {
      return { ...item, summary: toBulletHtml(lines.slice(0, 2)) };
    }
    const label = item.name?.trim() || "the project";
    const stack = (item.keywords ?? []).map((keyword) => keyword.trim()).filter(Boolean);
    return {
      ...item,
      summary: toBulletHtml([
        `Built ${label} and shipped it for real use`,
        stack.length > 0
          ? `Used ${stack.slice(0, 4).join(", ")} to get the work in front of people`
          : "Documented the work so others could pick it up",
      ]),
    };
  });

  next.summary = writeSummary(next);
  return next;
};
